/*
  Reels experience (sections/reels-experience.liquid)

  One product at a time, full screen. Each product shows its video (if any) and then
  the first photo of each colour, advancing automatically (photos every N ms, the video
  when it ends). Gestures:
    - swipe up / down     next / previous product
    - swipe left / right  next / previous photo (and stops the automatic advance)
    - press and hold      pause
    - pinch               zoom into the photo/video (springs back when released)
    - on the last photo, pulling further (a long swipe) loads the product's other
      photos, grouped by colour; a hint and a pull indicator guide the gesture
  Tapping a size adds that colour + size to the cart; the cart pill animates.
  Tapping the bottom bar opens the product page.
  After the last product comes an end screen (logo + link to the homepage).

  Loading: only the photo on screen and the next one are requested; the previous and
  next products are built ahead of time (first photo / video poster only) so a swipe
  shows them instantly. Videos only start streaming when they are on screen.
*/
(function () {
  var SWIPE = 40;
  var ANIMATION_MS = 400;
  var PULL_PX = 180; // how far to pull past the last photo to load more
  var IMAGE_WIDTHS = [540, 720, 900];
  var IMAGE_SIZES = '(min-width: 481px) 480px, 100vw';

  // Shopify CDN url (with width=720) -> url / srcset at other widths
  function sized(url, width) {
    return url.replace(/([?&])width=\d+/, '$1width=' + width);
  }
  function srcsetFor(url) {
    return IMAGE_WIDTHS.map(function (width) { return sized(url, width) + ' ' + width + 'w'; }).join(', ');
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function Reels(root) {
    this.root = root;
    this.stage = root.querySelector('.reels__stage');
    this.toast = root.querySelector('[data-toast]');
    this.cartCount = root.querySelector('[data-cart-count]');
    this.cartTotal = root.querySelector('[data-cart-total]');
    this.cartLink = root.querySelector('[data-cart]');
    this.moneyFormat = root.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    this.photoMs = parseInt(root.dataset.photoMs, 10) || 1000;
    this.autoNext = root.dataset.autoNext === 'true';
    this.auto = true;
    this.paused = false;
    this.timer = null;

    try {
      this.products = JSON.parse(root.querySelector('[data-reels-products]').textContent) || [];
    } catch (e) {
      this.products = [];
    }
    this.products.forEach(function (product) {
      product.slides = [];
      if (product.video && (product.video.hls || product.video.mp4) && !product.soldOut) {
        // The video's colour (from its photo group); single-colour products use that colour
        var videoSlide = { type: 'video' };
        var videoColor = -1;
        product.colors.forEach(function (color, index) {
          if (color.name === product.video.color) videoColor = index;
        });
        if (videoColor === -1 && product.colors.length === 1) videoColor = 0;
        if (videoColor !== -1) videoSlide.color = videoColor;
        // If the video's colour is sold out it isn't loaded at all: start on the next colour
        var colourSoldOut = videoColor === -1 && product.video.color;
        if (!colourSoldOut) product.slides.push(videoSlide);
      }
      product.colors.forEach(function (color, index) {
        product.slides.push({ type: 'photo', color: index });
      });
    });
    this.products = this.products.filter(function (product) {
      return product.slides.length > 0 && product.colors.length > 0;
    });
    if (!this.products.length) return;

    this.cartItems = parseInt((this.cartCount && this.cartCount.textContent) || '0', 10) || 0;
    this.cartCents = parseInt((this.cartTotal && this.cartTotal.dataset.cents) || '0', 10) || 0;
    this.updateCartSpace();
    var reels = this;
    window.addEventListener('resize', function () { reels.updateCartSpace(); });
    // Re-measure when the pill changes size (new total, fonts finishing loading...)
    if (window.ResizeObserver && this.cartLink) {
      new ResizeObserver(function () { reels.updateCartSpace(); }).observe(this.cartLink);
    }

    this.views = {};
    this.index = 0;
    this.current = this.viewFor(0);
    this.current.node.style.transform = 'translateY(0)';
    this.showSlide(0, 0);
    this.bind();

    var self = this;
    var idle = window.requestIdleCallback || function (callback) { return setTimeout(callback, 600); };
    idle(function () {
      self.prepareNeighbours();
      // Fetch the HLS player early (only where it is needed) so the first video starts at once
      var hasVideo = self.products.some(function (product) { return product.video; });
      if (hasVideo && window.VideoHLS && window.VideoHLS.warmup) window.VideoHLS.warmup();
    });
  }

  /* ---------- building ---------- */

  Reels.prototype.buildProduct = function (index) {
    var self = this;
    var product = this.products[index];
    var view = { index: index, product: product, slide: 0, slides: [], node: el('div', 'reels__product') };

    var media = el('div', 'reels__media');
    var progress = el('div', 'reels__progress');
    view.bars = [];
    media.appendChild(progress);

    view.media = media;
    view.progress = progress;
    product.slides.forEach(function (slide, slideIndex) {
      self.addSlideNode(view, slide, slideIndex);
    });

    view.sizes = el('div', 'reels__sizes');
    media.appendChild(view.sizes);

    if (product.soldOut) {
      media.appendChild(el('div', 'reels__soldout', this.root.dataset.soldOutText || ''));
    }

    view.hint = el('div', 'reels__hint');
    view.hint.setAttribute('aria-hidden', 'true');
    var arrow = el('span', 'reels__hint-arrow');
    arrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>' + '<svg width="16" height="16" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    // While pulling, the pill fills up from the right; when full it says to let go
    view.hint.appendChild(el('span', 'reels__hint-fill'));
    view.hint.appendChild(arrow);
    view.hintText = view.hint.appendChild(el('span', 'reels__hint-text', this.root.dataset.moreHint || ''));
    media.appendChild(view.hint);


    // The title link stretches over the whole bottom bar (opens the product page);
    // the other-colour swatches sit above it
    var bar = el('div', 'reels__bar');
    var info = el('div', 'reels__info');
    var title = el('a', 'reels__title', product.title);
    title.href = product.url;
    view.color = el('div', 'reels__color');
    info.appendChild(title);
    info.appendChild(view.color);
    view.price = el('div', 'reels__price');
    // Other colours sit between the product info and the price, within thumb reach
    view.others = el('div', 'reels__others');
    bar.appendChild(info);
    bar.appendChild(view.others);
    bar.appendChild(view.price);

    view.node.appendChild(media);
    view.node.appendChild(bar);
    view.node.style.transition = 'none';
    return view;
  };

  Reels.prototype.addSlideNode = function (view, slide, slideIndex) {
    var self = this;
    var product = view.product;
    var node = el('div', 'reels__slide');
    node.style.transition = 'none';
    node.style.transform = slideIndex === 0 ? 'translateX(0)' : 'translateX(100%)';
    if (slide.type === 'video') {
        var video = el('video');
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.preload = 'none';
        if (product.video.poster) video.poster = product.video.poster;
        video.addEventListener('ended', function () {
          if (self.current === view && view.slide === slideIndex) self.advance();
        });
        video.addEventListener('error', function () {
          if (self.current === view && view.slide === slideIndex && self.auto) self.advance();
        });
        node.appendChild(video);
        view.video = video;
    } else {
      var image = slide.image || (slide.type === 'photo' ? product.colors[slide.color].image : null);
      if (image) {
        // The file is only requested by loadSlide(), right before it is needed
        var img = el('img');
        var colorName = slide.type === 'photo' ? product.colors[slide.color].name : '';
        img.alt = product.title + (colorName ? ' - ' + colorName : '');
        img.decoding = 'async';
        img.dataset.src = image;
        node.appendChild(img);
      }
    }
    view.media.insertBefore(node, view.sizes || null);
    view.slides.push(node);
    view.bars = view.bars || [];
    view.bars.push(view.progress.appendChild(el('span')));
  };

  Reels.prototype.renderDetails = function (view) {
    var self = this;
    var slide = view.product.slides[view.slide];
    var known = typeof slide.color === 'number';
    var color = view.product.colors[known ? slide.color : 0];

    // When the colour on screen isn't known (video/photo outside the colour groups) hide colour and sizes
    view.color.hidden = !known;
    view.others.hidden = !known;
    view.sizes.hidden = !known || !!view.product.soldOut;
    if (view.renderedColor === color) return;
    view.renderedColor = color;

    this.renderColorRow(view, color);

    var first = color.sizes.filter(function (size) { return size.available !== false; })[0] || color.sizes[0];
    view.price.textContent = '';
    if (first) {
      view.price.appendChild(document.createTextNode(first.price));
      if (first.compare) view.price.appendChild(el('del', null, first.compare));
    }

    view.sizes.textContent = '';
    color.sizes.forEach(function (size) {
      var button = el('button', 'reels__size', size.label);
      button.type = 'button';
      // Long labels (e.g. "OneSize") get a smaller font so they fit the circle
      var length = String(size.label).length;
      if (length >= 7) button.classList.add('is-longer');
      else if (length >= 5) button.classList.add('is-long');
      button.setAttribute('aria-label', view.product.title + ' ' + color.name + ' ' + size.label);
      if (size.available === false) {
        // Sold out: shown crossed out, not clickable
        button.classList.add('is-soldout');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-label', button.getAttribute('aria-label') + ' - ' + (self.root.dataset.soldOutText || ''));
      } else {
        button.addEventListener('click', function () {
          self.addToCart(size, button);
        });
      }
      view.sizes.appendChild(button);
    });
  };

  function swatchNode(color) {
    var swatch = el('span', 'reels__swatch');
    if (color.swatch && color.swatch.img) swatch.style.backgroundImage = 'url("' + color.swatch.img + '")';
    else if (color.swatch && color.swatch.color) swatch.style.backgroundColor = color.swatch.color;
    return swatch;
  }

  // Current colour (swatch + name) followed by the other colours; tapping one jumps to it
  Reels.prototype.renderColorRow = function (view, color) {
    var self = this;
    var product = view.product;
    view.color.textContent = '';
    view.color.appendChild(swatchNode(color));
    view.color.appendChild(el('span', 'reels__color-name', color.name));

    var others = view.others;
    others.textContent = '';
    product.colors.forEach(function (other, colorIndex) {
      if (other === color) return;
      var button = el('button', 'reels__other');
      button.type = 'button';
      button.setAttribute('aria-label', other.name);
      button.appendChild(swatchNode(other));
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.jumpToColor(view, colorIndex);
      });
      others.appendChild(button);
    });
  };

  Reels.prototype.jumpToColor = function (view, colorIndex) {
    if (view !== this.current) return;
    var target = -1;
    view.product.slides.forEach(function (slide, index) {
      if (target === -1 && slide.type === 'photo' && slide.color === colorIndex) target = index;
    });
    if (target === -1 || target === view.slide) return;
    // Choosing a colour is a manual action: stop the automatic advance
    this.auto = false;
    this.clearTimer();
    if (view.video) view.video.loop = true;
    this.showSlide(target, target > view.slide ? 1 : -1);
  };

  Reels.prototype.loadSlide = function (view, slideIndex, priority) {
    var node = view.slides[slideIndex];
    if (!node) return;
    var img = node.querySelector('img[data-src]');
    if (img) {
      if (priority) img.setAttribute('fetchpriority', 'high');
      img.sizes = IMAGE_SIZES;
      img.srcset = srcsetFor(img.dataset.src);
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
  };

  // Returns the (cached) view of a product, adding it to the stage off screen
  // End screen shown after the last product
  Reels.prototype.buildEnd = function (index) {
    var data = this.root.dataset;
    var view = { index: index, isEnd: true, slides: [], slide: 0, node: el('div', 'reels__product reels__end') };
    var link = el('a', 'reels__end-link');
    link.href = data.endUrl || '/';
    if (data.endLogo) {
      var logo = el('img', 'reels__end-logo');
      logo.src = data.endLogo;
      if (data.endLogoSrcset) logo.srcset = data.endLogoSrcset;
      logo.sizes = '220px';
      logo.alt = data.endLogoAlt || '';
      link.appendChild(logo);
    }
    link.appendChild(el('span', 'reels__end-text', data.endText || ''));
    view.node.appendChild(link);
    view.node.style.transition = 'none';
    return view;
  };

  Reels.prototype.viewFor = function (index) {
    var view = this.views[index];
    if (!view && index === this.products.length) {
      view = this.views[index] = this.buildEnd(index);
      view.node.style.transform = 'translateY(' + (index > this.index ? '100%' : '-100%') + ')';
      this.stage.appendChild(view.node);
    }
    if (!view) {
      view = this.views[index] = this.buildProduct(index);
      view.node.style.transform = 'translateY(' + (index > this.index ? '100%' : '-100%') + ')';
      this.stage.appendChild(view.node);
      this.loadSlide(view, 0, index === this.index);
      if (view.video && view.product.video.poster) view.video.poster = view.product.video.poster;
    }
    return view;
  };

  // Keep the previous and next products ready; drop the rest (and their videos)
  Reels.prototype.prepareNeighbours = function () {
    var self = this;
    [this.index + 1, this.index - 1].forEach(function (index) {
      if (index >= 0 && index <= self.products.length) {
        var view = self.viewFor(index);
        if (view !== self.current) {
          view.node.style.transition = 'none';
          view.node.style.transform = 'translateY(' + (index > self.index ? '100%' : '-100%') + ')';
        }
      }
    });
    Object.keys(this.views).forEach(function (key) {
      var index = parseInt(key, 10);
      if (Math.abs(index - self.index) > 1) {
        self.destroyView(self.views[key]);
        delete self.views[key];
      }
    });
  };

  Reels.prototype.destroyView = function (view) {
    if (view.video) {
      if (view.video._hls) view.video._hls.destroy();
      view.video.removeAttribute('src');
      view.video.load();
    }
    view.node.remove();
  };

  /* ---------- slides ---------- */

  Reels.prototype.showSlide = function (slideIndex, direction) {
    var view = this.current;
    if (view.isEnd) return;
    var previous = view.slide;
    view.slide = slideIndex;

    view.slides.forEach(function (node, index) {
      if (direction !== 0 && (index === previous || index === slideIndex)) {
        node.style.transition = '';
      } else {
        node.style.transition = 'none';
      }
      // Upcoming photos wait on the right and slide in leftwards; past ones leave to the left
      if (index === slideIndex) node.style.transform = 'translateX(0)';
      else if (index < slideIndex) node.style.transform = 'translateX(-100%)';
      else node.style.transform = 'translateX(100%)';
    });
    view.bars.forEach(function (bar, index) {
      bar.classList.toggle('is-active', index === slideIndex);
    });

    if (view.video && previous !== slideIndex && view.product.slides[previous].type === 'video') {
      view.video.pause();
    }

    this.loadSlide(view, slideIndex, true);
    this.loadSlide(view, slideIndex + 1, false);
    this.renderDetails(view);
    this.updateHint(view);
    this.clearTimer();

    var slide = view.product.slides[slideIndex];
    if (slide.type === 'video') {
      this.playVideo(view);
    } else if (this.auto) {
      this.startTimer(this.photoMs);
    }
  };

  Reels.prototype.playVideo = function (view) {
    var self = this;
    var video = view.video;
    video.loop = !this.auto;
    if (!view.videoReady) {
      view.videoReady = true;
      video.preload = 'auto';
      var source = view.product.video;
      var useMp4 = function () {
        if (source.mp4) video.src = source.mp4;
      };
      var attach = window.VideoHLS && source.hls ? window.VideoHLS.attach(video, source.hls) : Promise.resolve(false);
      attach
        .then(function (usedHls) {
          if (!usedHls) useMp4();
        })
        .catch(useMp4)
        .then(function () {
          if (self.current === view && !self.paused) self.safePlay(video);
        });
    } else {
      try { video.currentTime = 0; } catch (e) {}
      if (!this.paused) this.safePlay(video);
    }
  };

  Reels.prototype.safePlay = function (video) {
    var self = this;
    var promise = video.play();
    if (promise && promise.catch) {
      promise.catch(function () {
        // Autoplay refused: move on to the photos instead of getting stuck
        if (self.auto && self.current && self.current.video === video) self.advance();
      });
    }
  };

  Reels.prototype.advance = function () {
    var view = this.current;
    if (view.slide < view.slides.length - 1) {
      this.showSlide(view.slide + 1, 1);
    } else if (this.auto && this.autoNext && view.index < this.products.length) {
      this.goProduct(1);
    }
  };

  Reels.prototype.manualSlide = function (direction) {
    if (this.current.isEnd) return;
    this.auto = false;
    this.clearTimer();
    if (this.current.video) this.current.video.loop = true;
    var view = this.current;
    var target = view.slide + direction;
    if (target >= view.slides.length) {
      // Past the last photo: bounce and nudge the "swipe for more" hint
      this.bounce(view);
      this.nudgeHint(view);
      return;
    }
    if (target < 0) {
      this.bounce(view, true);
      return;
    }
    this.showSlide(target, direction);
  };

  Reels.prototype.canLoadMore = function (view) {
    var state = view.product.morePhotos;
    return view.product.more && state !== 'done' && state !== 'loading';
  };

  Reels.prototype.updateHint = function (view) {
    if (!view.hint) return;
    var show = view.slide === view.slides.length - 1 && this.canLoadMore(view);
    view.hint.classList.toggle('is-visible', show);
    if (!show) view.hint.classList.remove('is-loading');
  };

  Reels.prototype.nudgeHint = function (view) {
    if (!view.hint || !view.hint.classList.contains('is-visible')) return;
    view.hint.classList.remove('is-nudge');
    void view.hint.offsetWidth;
    view.hint.classList.add('is-nudge');
  };

  // Live feedback while pulling past the last photo (distance in px, 0 = released)
  Reels.prototype.setPull = function (view, distance) {
    var node = view.slides[view.slide];
    var progress = Math.min(1, distance / PULL_PX);
    if (distance > 0) {
      node.style.transition = 'none';
      node.style.transform = 'translateX(' + (-distance * 0.35) + 'px)';
      var ready = progress >= 1;
      view.hint.style.setProperty('--p', progress);
      view.hint.classList.add('is-pulling');
      if (ready !== view.hint.classList.contains('is-ready')) {
        view.hint.classList.toggle('is-ready', ready);
        view.hintText.textContent = ready ? this.root.dataset.moreRelease || '' : this.root.dataset.moreHint || '';
      }
    } else {
      node.style.transition = '';
      node.style.transform = 'translateX(0)';
      view.hint.classList.remove('is-pulling', 'is-ready');
      view.hint.style.setProperty('--p', 0);
      view.hintText.textContent = this.root.dataset.moreHint || '';
    }
  };

  // Rebuilds a product's view after its slides changed (keeps its place on the stage)
  Reels.prototype.rebuildView = function (view) {
    var fresh = this.buildProduct(view.index);
    fresh.node.style.transform = view.node.style.transform;
    this.stage.insertBefore(fresh.node, view.node);
    this.loadSlide(fresh, 0, false);
    this.destroyView(view);
    this.views[view.index] = fresh;
    if (this.current === view) this.current = fresh;
    return fresh;
  };

  Reels.prototype.bounce = function (view, fromStart) {
    var node = view.slides[view.slide];
    if (!node || !node.animate) return;
    var offset = fromStart ? '24px' : '-24px';
    node.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(' + offset + ')' }, { transform: 'translateX(0)' }],
      { duration: 320, easing: 'ease-out' }
    );
  };

  // Fetches the product's other photos (only when the customer insists) and appends them
  Reels.prototype.loadMorePhotos = function (view) {
    var self = this;
    var product = view.product;
    if (product.morePhotos === 'loading' || product.morePhotos === 'done') return;
    product.morePhotos = 'loading';
    if (view.hint) view.hint.classList.add('is-loading');

    // Same photo can come as //shop-domain/cdn/shop/files/x.webp or cdn.shopify.com/.../files/x.webp:
    // compare by file name only
    var path = function (url) {
      return (url || '').split('?')[0].split('/').pop();
    };
    var shown = {};
    product.colors.forEach(function (color) {
      if (color.image) shown[path(color.image)] = true;
    });

    fetch(product.url.split('?')[0] + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        product.morePhotos = 'done';

        // Photos are grouped by colour: each colour's variants point at the first photo of
        // its group, and the photos that follow (until the next colour) belong to it.
        var colorIndexByName = {};
        product.colors.forEach(function (color, index) { colorIndexByName[color.name] = index; });
        var colorOption = -1;
        (data.options || []).forEach(function (option, index) {
          var values = option.values || [];
          if (colorOption === -1 && product.colors.some(function (color) { return values.indexOf(color.name) !== -1; })) {
            colorOption = index;
          }
        });
        var starts = {};
        (data.variants || []).forEach(function (variant) {
          var name = colorOption === -1 ? '' : variant.options[colorOption];
          var position = variant.featured_media && variant.featured_media.position;
          if (!position) return;
          if (!(name in starts) || position < starts[name]) starts[name] = position;
        });
        var groups = Object.keys(starts)
          .map(function (name) { return { name: name, start: starts[name] }; })
          .sort(function (a, b) { return a.start - b.start; });
        var colorForPosition = function (position) {
          var name = null;
          groups.forEach(function (group) { if (group.start <= position) name = group.name; });
          if (name === null && colorOption === -1) name = '';
          return name;
        };

        var extras = [];
        (data.media || []).forEach(function (media) {
          if (media.media_type !== 'image' || !media.src) return;
          var src = media.src.indexOf('//') === 0 ? 'https:' + media.src : media.src;
          if (shown[path(src)]) return;
          var name = colorForPosition(media.position);
          // Photos of a sold-out colour are skipped (that colour can't be added to the cart)
          if (name !== null && !(name in colorIndexByName)) return;
          shown[path(src)] = true;
          var slide = { image: src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=720' };
          if (name !== null) {
            slide.type = 'photo';
            slide.color = colorIndexByName[name];
          } else {
            slide.type = 'extra';
          }
          extras.push(slide);
        });

        if (!extras.length) {
          product.more = false;
          self.updateHint(view);
          return;
        }

        // Regroup: video first, then each colour's photo followed by the rest of its set
        var oldSlides = product.slides;
        var standing = oldSlides[view.slide];
        var grouped = oldSlides.filter(function (slide) { return slide.type === 'video'; });
        product.colors.forEach(function (color, colorIndex) {
          oldSlides.concat(extras).forEach(function (slide) {
            if (slide.type === 'photo' && slide.color === colorIndex) grouped.push(slide);
          });
        });
        extras.forEach(function (slide) { if (slide.type === 'extra') grouped.push(slide); });
        product.slides = grouped;

        var isCurrent = self.current === view;
        var fresh = self.rebuildView(view);
        if (!isCurrent) return;

        // Loading more is a manual action: stay on this product's photos
        self.auto = false;
        var from = grouped.indexOf(standing);
        fresh.slide = from;
        self.showSlide(from, 0);
        requestAnimationFrame(function () {
          if (self.current === fresh && from + 1 < fresh.slides.length) self.showSlide(from + 1, 1);
        });
      })
      .catch(function () {
        product.morePhotos = null;
        self.updateHint(view);
      });
  };

  /* ---------- products ---------- */

  Reels.prototype.goProduct = function (direction) {
    // Swiping up on the end screen closes the experience and goes to the homepage
    if (this.current.isEnd && direction > 0) {
      window.location.href = this.root.dataset.endUrl || '/';
      return;
    }
    var target = this.index + direction;
    // products.length is the end screen
    if (target < 0 || target > this.products.length || this.animating) return;
    this.animating = true;
    this.clearTimer();

    var old = this.current;
    if (old.video) old.video.pause();

    var next = this.viewFor(target);
    next.node.style.transition = 'none';
    next.node.style.transform = 'translateY(' + (direction > 0 ? '100%' : '-100%') + ')';
    void next.node.offsetHeight;
    next.node.style.transition = '';
    old.node.style.transition = '';
    next.node.style.transform = 'translateY(0)';
    old.node.style.transform = 'translateY(' + (direction > 0 ? '-100%' : '100%') + ')';

    this.index = target;
    this.current = next;
    this.root.classList.toggle('is-end', !!next.isEnd);
    next.slide = 0;
    this.showSlide(0, 0);

    var self = this;
    setTimeout(function () {
      // The product we left stays ready (paused, rewound) in case the customer swipes back
      if (old.video) {
        try { old.video.currentTime = 0; } catch (e) {}
      }
      old.node.style.transition = 'none';
      self.prepareNeighbours();
      self.animating = false;
    }, ANIMATION_MS);
  };

  /* ---------- timer / pause ---------- */

  Reels.prototype.startTimer = function (ms) {
    var self = this;
    this.clearTimer();
    this.timer = { remaining: ms, start: Date.now(), id: null };
    if (!this.paused) {
      this.timer.id = setTimeout(function () {
        self.timer = null;
        self.advance();
      }, ms);
    }
  };

  Reels.prototype.clearTimer = function () {
    if (this.timer && this.timer.id) clearTimeout(this.timer.id);
    this.timer = null;
  };

  Reels.prototype.pause = function () {
    if (this.paused) return;
    this.paused = true;
    if (this.timer && this.timer.id) {
      clearTimeout(this.timer.id);
      this.timer.id = null;
      this.timer.remaining -= Date.now() - this.timer.start;
    }
    if (this.current.video) this.current.video.pause();
  };

  Reels.prototype.resume = function () {
    if (!this.paused) return;
    this.paused = false;
    var self = this;
    if (this.timer) {
      this.timer.start = Date.now();
      this.timer.id = setTimeout(function () {
        self.timer = null;
        self.advance();
      }, Math.max(0, this.timer.remaining));
    }
    if (this.current.isEnd) return;
    var slide = this.current.product.slides[this.current.slide];
    if (slide.type === 'video' && this.current.video) this.safePlay(this.current.video);
  };

  /* ---------- cart ---------- */

  // Space taken by the cart pill, so the top hint sits between the close button and the cart
  Reels.prototype.updateCartSpace = function () {
    var visible = this.cartLink && !this.cartLink.hidden;
    var space = visible ? this.cartLink.offsetWidth + 24 : 56;
    this.root.style.setProperty('--reels-cart-space', space + 'px');
  };

  Reels.prototype.renderCart = function (animate) {
    if (this.cartCount) this.cartCount.textContent = this.cartItems;
    if (this.cartTotal) this.cartTotal.textContent = this.formatMoney(this.cartCents);
    if (this.cartLink) {
      // The cart only shows once it has something in it
      this.cartLink.hidden = this.cartItems === 0;
      if (animate) {
        // Restart the "added" animation on every add
        this.cartLink.classList.remove('is-added');
        void this.cartLink.offsetWidth;
        this.cartLink.classList.add('is-added');
      }
    }
    this.updateCartSpace();
  };

  // Optimistic add to cart: the pill updates instantly, the request runs in the background
  // and the real cart totals replace the estimate when it answers (or it is undone on error)
  Reels.prototype.addToCart = function (size, button) {
    var self = this;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cents = size.cents || 0;

    // Taps still waiting for an answer are kept on top of the server totals
    this.pendingItems = (this.pendingItems || 0) + 1;
    this.pendingCents = (this.pendingCents || 0) + cents;
    this.cartItems += 1;
    this.cartCents += cents;
    this.renderCart(true);
    var settle = function () {
      self.pendingItems -= 1;
      self.pendingCents -= cents;
    };
    if (navigator.vibrate) navigator.vibrate(15);

    // Requests run one after the other: simultaneous /cart/add.js calls overwrite each other
    this.cartQueue = (this.cartQueue || Promise.resolve()).then(function () {
      return fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: size.id, quantity: 1 }] })
      })
      .then(function (response) {
        if (!response.ok) throw new Error('add failed');
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        settle();
        self.cartItems = cart.item_count + self.pendingItems;
        self.cartCents = cart.total_price + self.pendingCents;
        self.renderCart(false);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {
        settle();
        self.cartItems = Math.max(0, self.cartItems - 1);
        self.cartCents = Math.max(0, self.cartCents - cents);
        self.renderCart(false);
        self.showToast(self.root.dataset.errorText, true);
      });
    });
  };

  // Minimal Shopify money formatter (cents -> shop money format)
  Reels.prototype.formatMoney = function (cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var thousands = function (separator) {
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    };
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, type) {
      switch (type) {
        case 'amount_no_decimals': return thousands(',');
        case 'amount_with_comma_separator': return thousands('.') + ',' + parts[1];
        case 'amount_no_decimals_with_comma_separator': return thousands('.');
        case 'amount_with_apostrophe_separator': return thousands("'") + '.' + parts[1];
        default: return thousands(',') + '.' + parts[1];
      }
    });
  };

  Reels.prototype.showToast = function (text, isError) {
    var toast = this.toast;
    toast.textContent = text;
    toast.classList.toggle('is-error', !!isError);
    toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(function () {
      toast.hidden = true;
    }, 1800);
  };

  /* ---------- input ---------- */

  Reels.prototype.bind = function () {
    var self = this;
    var start = null;

    var pulling = null;

    // ---- Pinch to zoom (two fingers). Like Instagram: zooms while pinching, springs back on release
    var pointers = new Map();
    var zoom = null;
    var distance = function (a, b) { return Math.hypot(a.x - b.x, a.y - b.y); };
    var midpoint = function (a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };

    var startZoom = function () {
      var view = self.current;
      if (view.isEnd) return;
      var node = view.slides[view.slide];
      var target = node && node.querySelector('img, video');
      if (!target) return;
      var points = Array.from(pointers.values());
      var rect = target.getBoundingClientRect();
      var mid = midpoint(points[0], points[1]);
      // Cancel any swipe / pull in progress: two fingers mean zoom
      if (pulling) { self.setPull(pulling, 0); pulling = null; }
      start = null;
      zoom = { target: target, startDistance: distance(points[0], points[1]) || 1, startMid: mid };
      target.style.transition = 'none';
      target.style.transformOrigin = (mid.x - rect.left) + 'px ' + (mid.y - rect.top) + 'px';
      self.pause();
    };

    var endZoom = function () {
      var target = zoom.target;
      zoom = null;
      target.style.transition = 'transform 250ms ease';
      target.style.transform = '';
      setTimeout(function () { target.style.transition = ''; }, 260);
      self.resume();
    };

    this.root.addEventListener('pointerdown', function (event) {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2 && !zoom) {
        startZoom();
        return;
      }
      if (zoom || pointers.size > 1) return;
      // Buttons and links keep their tap; the end-screen link still lets the customer swipe
      if (event.target.closest('button, a:not(.reels__end-link)')) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      self.pause();
    });

    this.root.addEventListener('pointermove', function (event) {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (!zoom || pointers.size < 2) return;
      var points = Array.from(pointers.values());
      var scale = Math.min(4, Math.max(1, distance(points[0], points[1]) / zoom.startDistance));
      var mid = midpoint(points[0], points[1]);
      zoom.target.style.transform = 'translate(' + (mid.x - zoom.startMid.x) + 'px, ' + (mid.y - zoom.startMid.y) + 'px) scale(' + scale + ')';
    });

    var releasePointer = function (event) {
      pointers.delete(event.pointerId);
      if (zoom && pointers.size < 2) endZoom();
    };
    this.root.addEventListener('pointerup', releasePointer);
    this.root.addEventListener('pointercancel', releasePointer);

    // On the last photo, a leftward drag becomes a "pull for more photos"
    this.root.addEventListener('pointermove', function (event) {
      if (!start || event.pointerId !== start.id) return;
      var view = self.current;
      var dx = event.clientX - start.x;
      var dy = event.clientY - start.y;
      if (!pulling) {
        if (dx < -20 && Math.abs(dx) > Math.abs(dy) && view.slide === view.slides.length - 1 && self.canLoadMore(view)) {
          pulling = view;
        } else {
          return;
        }
      }
      self.setPull(pulling, Math.max(0, -dx));
    });

    var finish = function (event, cancelled) {
      if (!start || event.pointerId !== start.id) return;
      var dx = event.clientX - start.x;
      var dy = event.clientY - start.y;
      start = null;
      self.resume();

      if (pulling) {
        var view = pulling;
        pulling = null;
        self.setPull(view, 0);
        self.auto = false;
        self.clearTimer();
        if (!cancelled && -dx >= PULL_PX) {
          view.hint.classList.add('is-loading');
          self.loadMorePhotos(view);
        } else {
          self.nudgeHint(view);
        }
        return;
      }
      if (cancelled) return;
      if (Math.abs(dy) > SWIPE && Math.abs(dy) > Math.abs(dx)) {
        self.goProduct(dy < 0 ? 1 : -1);
      } else if (Math.abs(dx) > SWIPE) {
        // Swipe right-to-left shows the next photo, left-to-right the previous one
        self.manualSlide(dx < 0 ? 1 : -1);
      }
    };
    this.root.addEventListener('pointerup', function (event) { finish(event, false); });
    this.root.addEventListener('pointercancel', function (event) { finish(event, true); });

    this.root.addEventListener('contextmenu', function (event) { event.preventDefault(); });

    // Close: go back to the page the customer came from on this site, else to the link set in the editor
    var close = this.root.querySelector('[data-close]');
    if (close) {
      close.addEventListener('click', function (event) {
        var sameSite = document.referrer && document.referrer.indexOf(location.origin) === 0;
        if (sameSite && history.length > 1) {
          event.preventDefault();
          history.back();
        }
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') self.goProduct(1);
      else if (event.key === 'ArrowUp') self.goProduct(-1);
      else if (event.key === 'ArrowRight') self.manualSlide(1);
      else if (event.key === 'ArrowLeft') self.manualSlide(-1);
    });

    var wheelLock = false;
    this.root.addEventListener('wheel', function (event) {
      if (wheelLock || Math.abs(event.deltaY) < 20) return;
      wheelLock = true;
      self.goProduct(event.deltaY > 0 ? 1 : -1);
      setTimeout(function () { wheelLock = false; }, 600);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
      else self.resume();
    });
  };

  function init() {
    document.querySelectorAll('.reels').forEach(function (root) {
      if (!root._reels) root._reels = new Reels(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
